import { last } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePopoverActionRef } from 'components/molecules/action-panel';
import { useTranslation } from 'hooks/use-translation';
import { CreatableEditableRefreshableContent, Empty } from 'stores/core';
import { Task } from 'stores/tasks';
import { DateField } from '../../fields';
import { OnUpdate } from '../bulk-editor';
import { Direction, getOptions, getTransformer, ShiftBy } from './model';

interface Props {
  startDate: DateField;
  dueDate: DateField;
  ids: string[];
  getLookup: () => Record<number, CreatableEditableRefreshableContent<Task>>;
  onUpdate: OnUpdate;
}

export const useShiftDatesState = (props?: Props) => {
  const [count, setCount] = useState(1);
  const [shiftBy, setShiftBy] = useState(ShiftBy.Days);
  const [options, setOptions] = useState(getOptions(shiftBy));

  useEffect(() => {
    setOptions(getOptions(shiftBy));
  }, [shiftBy, setOptions]);
  useEffect(() => {
    if (!options.includes(count)) {
      setCount(last(options) as number);
    }
  }, [options, count, setCount]);
  const [direction, setDirection] = useState(Direction.Later);
  const onCountChange = useCallback(
    (selected: unknown) => setCount(selected as number),
    [setCount],
  );
  const onShiftByChange = useCallback(
    (selected: unknown) => setShiftBy(selected as ShiftBy),
    [setShiftBy],
  );
  const onDirectionChange = useCallback(
    (selected: unknown) => setDirection(selected as Direction),
    [setDirection],
  );
  const innerRef = usePopoverActionRef();
  const saveTitle = useTranslation(
    'task_list.bulk_editor.options.shift_dates.save',
  );
  const save = useCallback(() => {
    if (!props) {
      return;
    }
    const { ids, getLookup, onUpdate, startDate, dueDate } = props;
    const transformer = getTransformer(
      count,
      shiftBy,
      direction,
    )([startDate, dueDate]);

    innerRef.current?.hide();
    onUpdate(
      ids
        .map((id) => getLookup()[id as unknown as number] ?? Empty())
        .map(([, x]) => x as Task)
        .filter(Boolean)
        .map(transformer),
    );
  }, [props, count, shiftBy, direction, innerRef]);
  const primary = useMemo(
    () => ({
      title: saveTitle,
      onClick: save,
    }),
    [save, saveTitle],
  );

  const clear = useCallback(() => {
    setCount(1);
    setShiftBy(ShiftBy.Days);
    setDirection(Direction.Later);
  }, [setCount, setShiftBy, setDirection]);

  const clearTitle = useTranslation('task_list.bulk_editor.clear');
  const secondary = useMemo(
    () => ({
      title: clearTitle,
      onClick: clear,
    }),
    [clear, clearTitle],
  );
  const cancelTitle = useTranslation('task_list.bulk_editor.cancel');
  const other = useMemo(
    () => [
      {
        title: cancelTitle,
        onClick: () => innerRef?.current?.hide(),
      },
    ],
    [innerRef, cancelTitle],
  );

  return {
    innerRef,
    clear,
    count,
    onCountChange,
    options,
    shiftBy,
    onShiftByChange,
    direction,
    onDirectionChange,
    primary,
    secondary,
    other,
  };
};
